import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  inject,
  Injectable,
  TemplateRef,
  Type,
} from '@angular/core';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';

export type AppDialogSize = 'sm' | 'md' | 'lg' | 'xl';

export interface AppDialogConfig<Data = unknown> extends Omit<
  DynamicDialogConfig<Data>,
  'data' | 'width'
> {
  data?: Data;
  size?: AppDialogSize;
  width?: string;
}

export class AppDialogRef<ComponentType = unknown, ResultType = unknown> {
  readonly closed: Observable<ResultType | undefined>;
  readonly destroyed: Observable<void>;

  constructor(
    private readonly dynamicDialogRef: DynamicDialogRef,
    readonly componentInstance?: ComponentType,
  ) {
    this.closed = dynamicDialogRef.onClose;
    this.destroyed = dynamicDialogRef.onDestroy;
  }

  close(result?: ResultType): void {
    this.dynamicDialogRef.close(result);
  }
}

interface TemplateDialogData {
  template: TemplateRef<unknown>;
}

@Component({
  template: `
    <ng-container *ngTemplateOutlet="config.data?.template ?? null" />
  `,
  imports: [NgTemplateOutlet],
})
class TemplateDialogComponent {
  protected readonly config = inject(DynamicDialogConfig<TemplateDialogData>);
}

@Injectable({ providedIn: 'root' })
export class AppDialogService {
  private readonly dialogService = inject(DialogService);

  open<ComponentType, Data = unknown, ResultType = unknown>(
    content: Type<ComponentType> | TemplateRef<unknown>,
    config: AppDialogConfig<Data> = {},
  ): AppDialogRef<ComponentType, ResultType> {
    const isTemplate = content instanceof TemplateRef;
    const {
      data,
      size = 'md',
      width,
      contentStyle,
      styleClass,
      ...dynamicConfig
    } = config;
    const dialogContent = (
      isTemplate ? TemplateDialogComponent : content
    ) as Type<unknown>;
    const dynamicDialogRef = this.dialogService.open(dialogContent, {
      showHeader: false,
      closable: false,
      modal: true,
      dismissableMask: true,
      closeOnEscape: true,
      focusOnShow: false,
      draggable: false,
      resizable: false,
      breakpoints: {
        '575px': 'calc(100vw - 1rem)',
      },
      ...dynamicConfig,
      width: width ?? this.widthFor(size),
      styleClass: ['nimmsta-dialog', styleClass].filter(Boolean).join(' '),
      contentStyle: {
        padding: '0',
        ...contentStyle,
      },
      data: isTemplate ? { template: content } : data,
    });

    if (!dynamicDialogRef) {
      throw new Error('PrimeNG failed to open dialog');
    }

    const componentInstance = isTemplate
      ? undefined
      : (this.dialogService.getInstance(dynamicDialogRef)?.componentRef
          ?.instance as ComponentType | undefined);

    return new AppDialogRef<ComponentType, ResultType>(
      dynamicDialogRef,
      componentInstance,
    );
  }

  private widthFor(size: AppDialogSize): string {
    switch (size) {
      case 'sm':
        return '300px';
      case 'lg':
        return '800px';
      case 'xl':
        return '1140px';
      default:
        return '500px';
    }
  }
}
